import React, { useState, useEffect, useCallback } from 'react';
import {
  SupportedLanguage,
  VocabularyLevel,
  WrongAnswerEntry,
  getLocalWrongAnswers,
  clearLocalWrongAnswer,
  markAsLearned,
  clearAllLocalWrongAnswers,
  LANGUAGE_CONFIG,
  LEVEL_CONFIG,
  QUESTION_TYPE_CONFIG
} from '../utils/multipleChoiceLogic';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import '../styles/WrongAnswerNotebook.css';

interface WrongAnswerNotebookProps {
  onClose: () => void;
  onStartPractice?: (entries: WrongAnswerEntry[]) => void;
}

interface NotebookStats {
  byLanguageLevel: Array<{
    language: string;
    level: string;
    total_items: number;
    total_wrong_attempts: number;
    learned_items: number;
  }>;
  totals: {
    total_items: number;
    total_wrong_attempts: number;
    learned_items: number;
  };
}

const WrongAnswerNotebook: React.FC<WrongAnswerNotebookProps> = ({ onClose, onStartPractice }) => {
  const { user, isAuthenticated } = useAuth();
  const [entries, setEntries] = useState<WrongAnswerEntry[]>([]);
  const [stats, setStats] = useState<NotebookStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage | 'all'>('all');
  const [selectedLevel, setSelectedLevel] = useState<VocabularyLevel | 'all'>('all');
  const [showLearned, setShowLearned] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'stats'>('list');

  // Fetch entries
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      if (isAuthenticated && user) {
        // Fetch from API
        const params: Record<string, string> = {};
        if (selectedLanguage !== 'all') params.language = selectedLanguage;
        if (selectedLevel !== 'all') params.level = selectedLevel;
        if (!showLearned) params.learned = 'false';

        const queryString = new URLSearchParams(params).toString();
        const response = await apiService.get(`/wrong-answers${queryString ? `?${queryString}` : ''}`);
        setEntries(response.wrongAnswers || []);
      } else {
        // Fetch from localStorage
        const localEntries = getLocalWrongAnswers(
          selectedLanguage !== 'all' ? selectedLanguage : undefined,
          selectedLevel !== 'all' ? selectedLevel : undefined
        );
        const filtered = showLearned
          ? localEntries
          : localEntries.filter(e => !e.correctAfterReview);
        setEntries(filtered);
      }
    } catch (error) {
      console.error('Failed to fetch wrong answers:', error);
      // Fall back to localStorage
      const localEntries = getLocalWrongAnswers(
        selectedLanguage !== 'all' ? selectedLanguage : undefined,
        selectedLevel !== 'all' ? selectedLevel : undefined
      );
      setEntries(localEntries);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, selectedLanguage, selectedLevel, showLearned]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (isAuthenticated && user) {
      try {
        const response = await apiService.get('/wrong-answers/stats');
        setStats(response);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    } else {
      // Calculate stats from localStorage
      const allEntries = getLocalWrongAnswers();
      const byLanguageLevel: NotebookStats['byLanguageLevel'] = [];

      const grouped = allEntries.reduce((acc, entry) => {
        const key = `${entry.language}-${entry.level}`;
        if (!acc[key]) {
          acc[key] = {
            language: entry.language,
            level: entry.level,
            total_items: 0,
            total_wrong_attempts: 0,
            learned_items: 0
          };
        }
        acc[key].total_items++;
        acc[key].total_wrong_attempts += entry.wrongAttempts;
        if (entry.correctAfterReview) acc[key].learned_items++;
        return acc;
      }, {} as Record<string, NotebookStats['byLanguageLevel'][0]>);

      Object.values(grouped).forEach(g => byLanguageLevel.push(g));

      setStats({
        byLanguageLevel,
        totals: {
          total_items: allEntries.length,
          total_wrong_attempts: allEntries.reduce((sum, e) => sum + e.wrongAttempts, 0),
          learned_items: allEntries.filter(e => e.correctAfterReview).length
        }
      });
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    fetchEntries();
    fetchStats();
  }, [fetchEntries, fetchStats]);

  // Mark as learned
  const handleMarkAsLearned = async (entry: WrongAnswerEntry) => {
    try {
      if (isAuthenticated && user) {
        await apiService.put(`/wrong-answers/${entry.id}/learned`, {});
      } else {
        markAsLearned(entry.id);
      }
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Failed to mark as learned:', error);
    }
  };

  // Delete entry
  const handleDelete = async (entry: WrongAnswerEntry) => {
    try {
      if (isAuthenticated && user) {
        await apiService.delete(`/wrong-answers/${entry.id}`);
      } else {
        clearLocalWrongAnswer(entry.id);
      }
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Failed to delete entry:', error);
    }
  };

  // Clear all
  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to clear all wrong answers? This cannot be undone.')) {
      return;
    }

    try {
      if (isAuthenticated && user) {
        const params: Record<string, string> = {};
        if (selectedLanguage !== 'all') params.language = selectedLanguage;
        if (selectedLevel !== 'all') params.level = selectedLevel;
        const queryString = new URLSearchParams(params).toString();
        await apiService.delete(`/wrong-answers${queryString ? `?${queryString}` : ''}`);
      } else {
        clearAllLocalWrongAnswers();
      }
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Failed to clear all:', error);
    }
  };

  return (
    <div className="wrong-answer-notebook">
      <div className="notebook-header">
        <h2>Wrong Answer Notebook</h2>
        <button className="close-button" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="notebook-tabs">
        <button
          className={`tab-button ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => setViewMode('list')}
        >
          Review List
        </button>
        <button
          className={`tab-button ${viewMode === 'stats' ? 'active' : ''}`}
          onClick={() => setViewMode('stats')}
        >
          Statistics
        </button>
      </div>

      {viewMode === 'stats' && stats && (
        <div className="notebook-stats">
          <div className="stats-summary">
            <div className="summary-card">
              <span className="summary-value">{stats.totals.total_items}</span>
              <span className="summary-label">Total Items</span>
            </div>
            <div className="summary-card">
              <span className="summary-value">{stats.totals.total_wrong_attempts}</span>
              <span className="summary-label">Total Wrong Attempts</span>
            </div>
            <div className="summary-card">
              <span className="summary-value">{stats.totals.learned_items}</span>
              <span className="summary-label">Marked as Learned</span>
            </div>
          </div>

          {stats.byLanguageLevel.length > 0 && (
            <div className="stats-breakdown">
              <h3>By Language & Level</h3>
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Language</th>
                    <th>Level</th>
                    <th>Items</th>
                    <th>Wrong Attempts</th>
                    <th>Learned</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.byLanguageLevel.map((row, index) => (
                    <tr key={index}>
                      <td>
                        {LANGUAGE_CONFIG[row.language as SupportedLanguage]?.flag}{' '}
                        {LANGUAGE_CONFIG[row.language as SupportedLanguage]?.name || row.language}
                      </td>
                      <td>
                        {LEVEL_CONFIG[row.level as VocabularyLevel]?.icon}{' '}
                        {LEVEL_CONFIG[row.level as VocabularyLevel]?.name || row.level}
                      </td>
                      <td>{row.total_items}</td>
                      <td>{row.total_wrong_attempts}</td>
                      <td>{row.learned_items}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {viewMode === 'list' && (
        <>
          <div className="notebook-filters">
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value as SupportedLanguage | 'all')}
            >
              <option value="all">All Languages</option>
              {Object.entries(LANGUAGE_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.flag} {config.name}
                </option>
              ))}
            </select>

            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value as VocabularyLevel | 'all')}
            >
              <option value="all">All Levels</option>
              {Object.entries(LEVEL_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.icon} {config.name}
                </option>
              ))}
            </select>

            <label className="show-learned-toggle">
              <input
                type="checkbox"
                checked={showLearned}
                onChange={(e) => setShowLearned(e.target.checked)}
              />
              Show Learned
            </label>
          </div>

          <div className="notebook-actions">
            {entries.length > 0 && (
              <>
                <button
                  className="action-button practice"
                  onClick={() => onStartPractice?.(entries)}
                  disabled={!onStartPractice}
                >
                  Practice ({entries.length})
                </button>
                <button className="action-button clear" onClick={handleClearAll}>
                  Clear All
                </button>
              </>
            )}
          </div>

          {loading ? (
            <div className="loading-state">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="empty-state">
              <p>No wrong answers recorded yet.</p>
              <p>Play some Multiple Choice quizzes and your mistakes will appear here for review!</p>
            </div>
          ) : (
            <div className="entries-list">
              {entries.map((entry) => (
                <div
                  key={entry.id}
                  className={`entry-card ${entry.correctAfterReview ? 'learned' : ''}`}
                >
                  <div className="entry-header">
                    <span className="entry-language">
                      {LANGUAGE_CONFIG[entry.language]?.flag}
                    </span>
                    <span className="entry-level">
                      {LEVEL_CONFIG[entry.level]?.icon}
                    </span>
                    <span className="entry-type">
                      {QUESTION_TYPE_CONFIG[entry.questionType]?.icon}
                    </span>
                    <span className="entry-attempts">
                      {entry.wrongAttempts} wrong
                    </span>
                  </div>

                  <div className="entry-content">
                    <div className="entry-target">{entry.vocabularyItem.target}</div>
                    <div className="entry-english">{entry.vocabularyItem.english}</div>
                    {entry.vocabularyItem.romanization && (
                      <div className="entry-romanization">{entry.vocabularyItem.romanization}</div>
                    )}
                    {entry.userWrongAnswers && entry.userWrongAnswers.length > 0 && (
                      <div className="entry-wrong-answers">
                        Your answers: {entry.userWrongAnswers.slice(-3).join(', ')}
                      </div>
                    )}
                  </div>

                  <div className="entry-actions">
                    {!entry.correctAfterReview && (
                      <button
                        className="entry-action learned"
                        onClick={() => handleMarkAsLearned(entry)}
                        title="Mark as learned"
                      >
                        ✓
                      </button>
                    )}
                    <button
                      className="entry-action delete"
                      onClick={() => handleDelete(entry)}
                      title="Delete"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WrongAnswerNotebook;
